import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

// 위 휴대폰의 그림자 레이어. 휴대폰 위치가 같아서 화면에 어떤 앱이 들어가든 같은 그림자를 쓴다.
// (operate_example_front_shadow.png 에서 예전 화면 내용이 얼룩으로 남아 있던 부분을 걷어 낸 것)
import FrontShadowLayer from "src/assets/images/hero/front_shadow.png";

// 마우스 위치에 따라 목업이 기울어지는 최대 각도(deg)와 따라 움직이는 최대 거리(px)
const MAX_TILT = 6;
const MAX_SHIFT = 14;

const TILT_PROPERTIES = ["--tilt-x", "--tilt-y", "--shift-x", "--shift-y"];

// 목업이 들어 있는 섹션에 핸들러를 걸어 두면, 섹션 위 포인터 위치에 따라 목업이 기울어진다.
export function useHeroMockupTilt() {
  const mockupRef = useRef<HTMLDivElement>(null);

  const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const mockup = mockupRef.current;
    if (!mockup || e.pointerType === "touch") return;

    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    // 섹션 중앙을 0으로 하는 -1 ~ 1 범위의 포인터 위치
    const x = Math.max(-1, Math.min(1, ((e.clientX - left) / width) * 2 - 1));
    const y = Math.max(-1, Math.min(1, ((e.clientY - top) / height) * 2 - 1));

    mockup.style.setProperty("--tilt-x", `${(-y * MAX_TILT).toFixed(2)}deg`);
    mockup.style.setProperty("--tilt-y", `${(x * MAX_TILT).toFixed(2)}deg`);
    mockup.style.setProperty("--shift-x", `${(x * MAX_SHIFT).toFixed(1)}px`);
    mockup.style.setProperty("--shift-y", `${(y * MAX_SHIFT).toFixed(1)}px`);
  };

  const handlePointerLeave = () => {
    TILT_PROPERTIES.forEach((property) => mockupRef.current?.style.removeProperty(property));
  };

  return { mockupRef, handlePointerMove, handlePointerLeave };
}

interface HeroMockupProps {
  mockupRef: React.RefObject<HTMLDivElement>;
  // 휴대폰별로 분리한 레이어 (아래 휴대폰 / 위 휴대폰). 각 화면에는 실제 앱 캡처가 들어 있다.
  back: string;
  front: string;
  alt: string;
}

// 겹쳐 놓인 휴대폰 두 대. 위 휴대폰이 오른쪽에서 들어와 아래 휴대폰 위에 겹쳐진다.
function HeroMockup({ mockupRef, back, front, alt }: HeroMockupProps) {
  // 레이어 이미지가 모두 준비된 뒤에 위 휴대폰과 그 그림자를 함께 보여준다. (먼저 받아진 그림자만 떠 보이지 않도록)
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const images = Array.from(mockupRef.current?.querySelectorAll("img") ?? []);

    Promise.all(images.map((image) => (image.decode ? image.decode().catch(() => undefined) : undefined))).then(() => {
      if (!isCancelled) setIsReady(true);
    });

    return () => {
      isCancelled = true;
    };
  }, [mockupRef]);

  return (
    <S.Float>
      <S.Mockup ref={mockupRef} data-ready={isReady}>
        <S.Layer src={back} alt={alt} />
        <S.FrontShadow src={FrontShadowLayer} alt="" />
        <S.Front src={front} alt="" />
      </S.Mockup>
    </S.Float>
  );
}

export default HeroMockup;

const float = keyframes`
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-16px);
  }
`;

// 위 휴대폰이 오른쪽에서 들어와 아래 휴대폰 위에 겹쳐진다.
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(520px, 0, 0);
  }
  35% {
    opacity: 1;
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

// 위 휴대폰이 자리를 잡으면서 아래 휴대폰과 바닥에 그림자가 드리워진다.
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const S = {
  Float: styled.div`
    animation: ${float} 6s ease-in-out infinite;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  Mockup: styled.div`
    position: relative;
    width: 1101px;
    height: 800px;
    pointer-events: none;
    transform: perspective(1600px) translate3d(var(--shift-x, 0px), var(--shift-y, 0px), 0) rotateX(var(--tilt-x, 0deg))
      rotateY(var(--tilt-y, 0deg));
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform;
    @media (prefers-reduced-motion: reduce) {
      transform: none;
      transition: none;
    }
  `,
  Layer: styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `,
  FrontShadow: styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    [data-ready="true"] > & {
      animation: ${fadeIn} 0.8s ease-out 0.6s both;
    }
    @media (prefers-reduced-motion: reduce) {
      [data-ready="true"] > & {
        opacity: 1;
        animation: none;
      }
    }
  `,
  Front: styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    [data-ready="true"] > & {
      animation: ${slideIn} 1.4s cubic-bezier(0.22, 1, 0.36, 1) both;
    }
    @media (prefers-reduced-motion: reduce) {
      [data-ready="true"] > & {
        opacity: 1;
        animation: none;
      }
    }
  `,
};
